import React, { useState, useEffect } from 'react';
import { payPeriodAPI, biweeklyTimesheetAPI } from '../services/api';
import type { PayPeriod, BiweeklyTimesheet as BiweeklyTimesheetType, BiweeklyTimesheetDetail, UserTimeEntry } from '../types';
import { useAuth } from '../contexts/AuthContext';

const PERIODS_TO_SHOW = 4; // current + 3 past

/**
 * BiweeklyTimesheet - View-only summary of time entries for hourly employees
 *
 * Displays time entries for a selected pay period (default: current), grouped by week.
 * Includes a period selector so employees can view and submit previous periods.
 * Time entry is done on the Time Entry screen; this page is for viewing and submitting for approval.
 */
const BiweeklyTimesheet: React.FC = () => {
  const { user } = useAuth();
  const [periods, setPeriods] = useState<PayPeriod[]>([]);
  const [currentPeriod, setCurrentPeriod] = useState<PayPeriod | null>(null);
  const [selectedPeriod, setSelectedPeriod] = useState<PayPeriod | null>(null);
  const [timesheet, setTimesheet] = useState<BiweeklyTimesheetDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [pendingTimesheets, setPendingTimesheets] = useState<BiweeklyTimesheetType[]>([]);
  const [rejectedTimesheets, setRejectedTimesheets] = useState<BiweeklyTimesheetType[]>([]);
  const [allMyTimesheets, setAllMyTimesheets] = useState<BiweeklyTimesheetType[]>([]);

  const loadTimesheetForPeriod = async (period: PayPeriod) => {
    if (!period.id) return;
    try {
      const res = await biweeklyTimesheetAPI.getForPeriod(period.id);
      setTimesheet(res.data);
    } catch (err: any) {
      console.error('Failed to load timesheet for period:', err);
      setTimesheet(null);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      setError(null);

      let current: PayPeriod | null = null;
      try {
        const periodRes = await payPeriodAPI.getCurrent();
        current = periodRes.data;
        setCurrentPeriod(current);
      } catch (err: any) {
        if (err.response?.status !== 404) throw err;
        setCurrentPeriod(null);
      }

      const periodsRes = await payPeriodAPI.getAll({ page_size: 10 });
      const results = periodsRes.data.results || [];
      const today = new Date().toISOString().slice(0, 10); // YYYY-MM-DD
      const currentAndPast = results.filter(
        (p: PayPeriod) => p.start_date <= today
      );
      const recentPeriods = currentAndPast.slice(0, PERIODS_TO_SHOW);
      setPeriods(recentPeriods);

      const initialPeriod = current ?? recentPeriods[0] ?? null;
      setSelectedPeriod(initialPeriod);
      if (initialPeriod) {
        await loadTimesheetForPeriod(initialPeriod);
      } else {
        setTimesheet(null);
      }

      await fetchNotifications();
    } catch (err: any) {
      console.error('Failed to fetch data:', err);
      setError(err.response?.data?.error || 'Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const fetchNotifications = async () => {
    try {
      const [submittedRes, draftRes] = await Promise.all([
        biweeklyTimesheetAPI.getAll({ status: 'submitted' }),
        biweeklyTimesheetAPI.getAll({ status: 'draft' }),
      ]);
      const submittedResults = submittedRes.data.results || [];
      const draftResults = draftRes.data.results || [];
      const ownSubmitted = submittedResults.filter((ts: BiweeklyTimesheetType) => ts.user === user?.id);
      const rejected = draftResults.filter(
        (ts: BiweeklyTimesheetType) => ts.user === user?.id && ts.rejection_reason
      );
      setPendingTimesheets(ownSubmitted);
      setRejectedTimesheets(rejected);
      const allMine = [
        ...ownSubmitted,
        ...draftResults.filter((ts: BiweeklyTimesheetType) => ts.user === user?.id),
      ];
      const approvedRes = await biweeklyTimesheetAPI.getAll({ status: 'approved' });
      const ownApproved = (approvedRes.data.results || []).filter(
        (ts: BiweeklyTimesheetType) => ts.user === user?.id
      );
      setAllMyTimesheets([...allMine, ...ownApproved]);
    } catch (err) {
      console.error('Failed to fetch notifications:', err);
    }
  };

  const handlePeriodChange = async (period: PayPeriod) => {
    setSelectedPeriod(period);
    await loadTimesheetForPeriod(period);
  };

  const handleViewAndResubmit = async (ts: BiweeklyTimesheetType) => {
    let period: PayPeriod | null = ts.pay_period_details ?? null;
    if (!period && ts.pay_period) {
      const res = await payPeriodAPI.getById(ts.pay_period);
      period = res.data;
    }
    if (!period?.id) return;
    setSelectedPeriod(period);
    if (!periods.some(p => p.id === period!.id)) {
      setPeriods(prev => [period!, ...prev]);
    }
    await loadTimesheetForPeriod(period);
  };

  const handleSubmitTimesheet = async () => {
    if (!timesheet?.id) return;
    if (!window.confirm('Are you sure you want to submit this timesheet for approval?')) return;
    try {
      await biweeklyTimesheetAPI.submit(timesheet.id);
      if (selectedPeriod?.id) {
        const timesheetRes = await biweeklyTimesheetAPI.getForPeriod(selectedPeriod.id);
        setTimesheet(timesheetRes.data);
      }
      await fetchNotifications();
      alert('Timesheet submitted successfully!');
    } catch (err: any) {
      console.error('Failed to submit timesheet:', err);
      alert(err.response?.data?.error || 'Failed to submit timesheet');
    }
  };

  const groupEntriesByWeek = (entries: UserTimeEntry[]) => {
    if (!entries || !selectedPeriod) return { week1: [], week2: [] };
    const periodStart = new Date(selectedPeriod.start_date + 'T00:00:00');
    const week1End = new Date(periodStart);
    week1End.setDate(week1End.getDate() + 6); // First 7 days
    
    const week1: UserTimeEntry[] = [];
    const week2: UserTimeEntry[] = [];
    
    entries.forEach(entry => {
      const entryDate = new Date(entry.date + 'T00:00:00');
      if (entryDate <= week1End) {
        week1.push(entry);
      } else {
        week2.push(entry);
      }
    });
    
    return { week1, week2 };
  };

  const calculateTotalHours = (entries: UserTimeEntry[]) => {
    return entries.reduce((sum, entry) => sum + parseFloat(entry.hours?.toString() || '0'), 0);
  };

  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr + 'T00:00:00');
    return date.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
  };

  const formatPeriodDates = (period: PayPeriod) => {
    const start = new Date(period.start_date + 'T00:00:00');
    const end = new Date(period.end_date + 'T00:00:00');
    return `${start.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} - ${end.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}`;
  };

  const getWeekDateRange = (weekNum: 1 | 2) => {
    if (!selectedPeriod) return '';
    const start = new Date(selectedPeriod.start_date + 'T00:00:00');
    if (weekNum === 1) {
      const end = new Date(start);
      end.setDate(end.getDate() + 6);
      return `${start.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} - ${end.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}`;
    } else {
      const week2Start = new Date(start);
      week2Start.setDate(week2Start.getDate() + 7);
      const end = new Date(selectedPeriod.end_date + 'T00:00:00');
      return `${week2Start.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} - ${end.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}`;
    }
  };

  const getStatusForPeriod = (periodId: number | undefined): string | null => {
    if (!periodId) return null;
    const ts = allMyTimesheets.find(t => t.pay_period === periodId || t.pay_period_details?.id === periodId);
    return ts?.status ?? null;
  };

  const periodOptions = ((): PayPeriod[] => {
    if (!selectedPeriod) return periods;
    if (periods.some(p => p.id === selectedPeriod.id)) return periods;
    return [selectedPeriod, ...periods];
  })();

  const getStatusBadge = (status: string) => {
    const styles: { [key: string]: { bg: string; color: string } } = {
      draft: { bg: '#e3f2fd', color: '#1565c0' },
      submitted: { bg: '#fff3e0', color: '#e65100' },
      approved: { bg: '#e8f5e9', color: '#2e7d32' },
    };
    const style = styles[status] || styles.draft;
    
    return (
      <span style={{
        padding: '0.25rem 0.75rem',
        backgroundColor: style.bg,
        color: style.color,
        borderRadius: '4px',
        fontWeight: 'bold',
        textTransform: 'capitalize',
      }}>
        {status}
      </span>
    );
  };

  if (loading) {
    return (
      <div className="container">
        <div className="card">
          <h2>Biweekly Timesheet</h2>
          <p>Loading...</p>
        </div>
      </div>
    );
  }

  const { week1, week2 } = groupEntriesByWeek(timesheet?.time_entries || []);
  const week1Total = calculateTotalHours(week1);
  const week2Total = calculateTotalHours(week2);
  const grandTotal = week1Total + week2Total;

  return (
    <div className="container">
      {/* Pending Approval Notifications */}
      {pendingTimesheets.length > 0 && (
        <div style={{
          padding: '1rem',
          backgroundColor: '#fff3e0',
          borderLeft: '4px solid #ff9800',
          marginBottom: '1rem',
          borderRadius: '4px',
        }}>
          <strong style={{ color: '#e65100' }}>Pending Approval</strong>
          <p style={{ margin: '0.5rem 0 0 0', color: '#666' }}>
            You have {pendingTimesheets.length} biweekly timesheet(s) awaiting approval.
          </p>
        </div>
      )}

      {/* Rejected Timesheet Notifications */}
      {rejectedTimesheets.map((ts) => (
        <div key={ts.id} style={{
          padding: '1rem',
          backgroundColor: '#ffebee',
          borderLeft: '4px solid #f44336',
          marginBottom: '1rem',
          borderRadius: '4px',
        }}>
          <strong style={{ color: '#c62828' }}>Timesheet Rejected</strong>
          <p style={{ margin: '0.5rem 0 0 0' }}>
            <strong>Period:</strong> {ts.pay_period_details?.name}
          </p>
          <p style={{ margin: '0.5rem 0 0 0' }}>
            <strong>Reason:</strong> {ts.rejection_reason}
          </p>
          <p style={{ margin: '0.5rem 0 0 0', color: '#666', fontSize: '0.9rem' }}>
            Please update your time entries and resubmit.
          </p>
          <button
            type="button"
            className="btn btn-primary"
            style={{ marginTop: '0.75rem' }}
            onClick={() => handleViewAndResubmit(ts)}
          >
            View and resubmit
          </button>
        </div>
      ))}

      <div className="card">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: '1rem', marginBottom: '1.5rem' }}>
          <div style={{ flex: '1 1 auto' }}>
            <h2 style={{ marginBottom: '0.5rem' }}>Biweekly Timesheet</h2>
            {periodOptions.length > 0 && (
              <div style={{ marginTop: '0.5rem' }}>
                <label htmlFor="biweekly-period-select" style={{ marginRight: '0.5rem', color: '#666', fontSize: '0.9rem' }}>
                  Pay period:
                </label>
                <select
                  id="biweekly-period-select"
                  value={selectedPeriod?.id ?? ''}
                  onChange={(e) => {
                    const id = Number(e.target.value);
                    const period = periodOptions.find(p => p.id === id) ?? null;
                    if (period) handlePeriodChange(period);
                  }}
                  style={{ minWidth: '220px', padding: '0.35rem 0.5rem' }}
                >
                  {periodOptions.map((p) => {
                    const status = getStatusForPeriod(p.id);
                    const label = formatPeriodDates(p);
                    const currentLabel = currentPeriod?.id === p.id ? ' (current)' : '';
                    const statusLabel = status ? ` — ${status}` : '';
                    return (
                      <option key={p.id} value={p.id!}>
                        {label}{currentLabel}{statusLabel}
                      </option>
                    );
                  })}
                </select>
              </div>
            )}
            {selectedPeriod && (
              <p style={{ margin: '0.5rem 0 0 0', color: '#666', fontSize: '0.9rem' }}>
                {formatPeriodDates(selectedPeriod)}
              </p>
            )}
          </div>
          {timesheet && (
            <div style={{ textAlign: 'right' }}>
              {getStatusBadge(timesheet.status)}
            </div>
          )}
        </div>

        {error && <div className="error" style={{ marginBottom: '1rem' }}>{error}</div>}

        {!selectedPeriod ? (
          <div style={{ 
            padding: '2rem', 
            textAlign: 'center', 
            backgroundColor: '#f5f5f5', 
            borderRadius: '8px' 
          }}>
            <p style={{ fontSize: '1.1rem', color: '#666' }}>
              No active pay period found for the current date.
            </p>
            <p style={{ color: '#999' }}>
              Please contact your administrator to set up pay periods.
            </p>
          </div>
        ) : (
          <>
            {/* Info Banner */}
            <div style={{
              padding: '1rem',
              backgroundColor: '#e3f2fd',
              borderRadius: '8px',
              marginBottom: '1.5rem',
              display: 'flex',
              alignItems: 'center',
              gap: '1rem',
            }}>
              <span style={{ fontSize: '1.5rem' }}>ℹ️</span>
              <div>
                <strong>This is a summary view of your time entries.</strong>
                <p style={{ margin: '0.25rem 0 0 0', color: '#666', fontSize: '0.9rem' }}>
                  To add or edit time entries, use the <a href="/time-entry" style={{ color: '#1976d2' }}>Time Entry</a> page.
                </p>
              </div>
            </div>

            {/* Hours Summary Box */}
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(3, 1fr)',
              gap: '1rem',
              marginBottom: '1.5rem',
            }}>
              <div style={{
                padding: '1.5rem',
                backgroundColor: '#f5f5f5',
                borderRadius: '8px',
                textAlign: 'center',
              }}>
                <div style={{ fontSize: '2rem', fontWeight: 'bold', color: '#1976d2' }}>
                  {week1Total.toFixed(2)}
                </div>
                <div style={{ color: '#666', fontSize: '0.9rem' }}>Week 1 Hours</div>
                <div style={{ color: '#999', fontSize: '0.8rem' }}>{getWeekDateRange(1)}</div>
              </div>
              <div style={{
                padding: '1.5rem',
                backgroundColor: '#f5f5f5',
                borderRadius: '8px',
                textAlign: 'center',
              }}>
                <div style={{ fontSize: '2rem', fontWeight: 'bold', color: '#1976d2' }}>
                  {week2Total.toFixed(2)}
                </div>
                <div style={{ color: '#666', fontSize: '0.9rem' }}>Week 2 Hours</div>
                <div style={{ color: '#999', fontSize: '0.8rem' }}>{getWeekDateRange(2)}</div>
              </div>
              <div style={{
                padding: '1.5rem',
                backgroundColor: '#e8f5e9',
                borderRadius: '8px',
                textAlign: 'center',
                border: '2px solid #4caf50',
              }}>
                <div style={{ fontSize: '2rem', fontWeight: 'bold', color: '#2e7d32' }}>
                  {grandTotal.toFixed(2)}
                </div>
                <div style={{ color: '#666', fontSize: '0.9rem' }}>Total Hours</div>
                <div style={{ color: '#999', fontSize: '0.8rem' }}>Pay Period</div>
              </div>
            </div>

            {/* Week 1 Table */}
            <div style={{ marginBottom: '1.5rem' }}>
              <h3 style={{ 
                marginBottom: '0.75rem', 
                padding: '0.5rem 1rem',
                backgroundColor: '#e3f2fd',
                borderRadius: '4px',
                display: 'flex',
                justifyContent: 'space-between',
              }}>
                <span>Week 1</span>
                <span style={{ fontWeight: 'normal', color: '#666' }}>{getWeekDateRange(1)}</span>
              </h3>
              {week1.length > 0 ? (
                <table>
                  <thead>
                    <tr>
                      <th>Date</th>
                      <th>Project</th>
                      <th>Rate</th>
                      <th style={{ textAlign: 'right' }}>Hours</th>
                    </tr>
                  </thead>
                  <tbody>
                    {week1
                      .sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime())
                      .map((entry) => (
                        <tr key={entry.id}>
                          <td>{formatDate(entry.date)}</td>
                          <td>{entry.project_code}</td>
                          <td>{entry.billing_rate_name}</td>
                          <td style={{ textAlign: 'right', fontWeight: 'bold' }}>
                            {parseFloat(entry.hours?.toString() || '0').toFixed(2)}
                          </td>
                        </tr>
                      ))}
                  </tbody>
                  <tfoot>
                    <tr style={{ backgroundColor: '#f5f5f5', fontWeight: 'bold' }}>
                      <td colSpan={3}>Week 1 Total</td>
                      <td style={{ textAlign: 'right' }}>{week1Total.toFixed(2)}</td>
                    </tr>
                  </tfoot>
                </table>
              ) : (
                <p style={{ color: '#999', textAlign: 'center', padding: '1rem' }}>
                  No time entries for Week 1
                </p>
              )}
            </div>

            {/* Week 2 Table */}
            <div style={{ marginBottom: '1.5rem' }}>
              <h3 style={{ 
                marginBottom: '0.75rem', 
                padding: '0.5rem 1rem',
                backgroundColor: '#e3f2fd',
                borderRadius: '4px',
                display: 'flex',
                justifyContent: 'space-between',
              }}>
                <span>Week 2</span>
                <span style={{ fontWeight: 'normal', color: '#666' }}>{getWeekDateRange(2)}</span>
              </h3>
              {week2.length > 0 ? (
                <table>
                  <thead>
                    <tr>
                      <th>Date</th>
                      <th>Project</th>
                      <th>Rate</th>
                      <th style={{ textAlign: 'right' }}>Hours</th>
                    </tr>
                  </thead>
                  <tbody>
                    {week2
                      .sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime())
                      .map((entry) => (
                        <tr key={entry.id}>
                          <td>{formatDate(entry.date)}</td>
                          <td>{entry.project_code}</td>
                          <td>{entry.billing_rate_name}</td>
                          <td style={{ textAlign: 'right', fontWeight: 'bold' }}>
                            {parseFloat(entry.hours?.toString() || '0').toFixed(2)}
                          </td>
                        </tr>
                      ))}
                  </tbody>
                  <tfoot>
                    <tr style={{ backgroundColor: '#f5f5f5', fontWeight: 'bold' }}>
                      <td colSpan={3}>Week 2 Total</td>
                      <td style={{ textAlign: 'right' }}>{week2Total.toFixed(2)}</td>
                    </tr>
                  </tfoot>
                </table>
              ) : (
                <p style={{ color: '#999', textAlign: 'center', padding: '1rem' }}>
                  No time entries for Week 2
                </p>
              )}
            </div>

            {/* Submit Button */}
            {timesheet && timesheet.can_submit && (
              <div style={{ 
                padding: '1rem', 
                backgroundColor: '#e8f5e9', 
                borderRadius: '8px',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
              }}>
                <div>
                  <strong style={{ color: '#2e7d32' }}>Ready to submit for payroll?</strong>
                  <p style={{ margin: '0.25rem 0 0 0', color: '#666', fontSize: '0.9rem' }}>
                    Make sure all your time entries are complete before submitting.
                  </p>
                </div>
                <button
                  onClick={handleSubmitTimesheet}
                  className="btn btn-success"
                >
                  Submit for Approval
                </button>
              </div>
            )}

            {timesheet && timesheet.status === 'submitted' && (
              <div style={{ 
                padding: '1rem', 
                backgroundColor: '#fff3e0', 
                borderRadius: '8px',
                textAlign: 'center',
              }}>
                <strong style={{ color: '#e65100' }}>⏳ Awaiting Approval</strong>
                <p style={{ margin: '0.5rem 0 0 0', color: '#666' }}>
                  Your timesheet has been submitted and is pending approval.
                </p>
              </div>
            )}

            {timesheet && timesheet.status === 'approved' && (
              <div style={{ 
                padding: '1rem', 
                backgroundColor: '#e8f5e9', 
                borderRadius: '8px',
                textAlign: 'center',
              }}>
                <strong style={{ color: '#2e7d32' }}>✓ Approved</strong>
                <p style={{ margin: '0.5rem 0 0 0', color: '#666' }}>
                  This timesheet has been approved by {timesheet.approved_by_username}.
                </p>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default BiweeklyTimesheet;
